import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState, type KeyboardEvent } from "react";
import * as Yup from "yup";
import { useDebounce } from "../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../queries/catalog/use-get-catalog-code";
import { useCreateFeeSchedule } from "../../../../queries/fee-scheduling/use-fee-schedule-create";
import { useUpdateFeeSchedule } from "../../../../queries/fee-scheduling/use-fee-schedule-update";
import useGetModifiers from "../../../../queries/modifiers/use-get-modifiers";
import { useProvidersList } from "../../../../queries/provider/setting/use-providers-list";
import { useInsurancePayerList } from "../../../../queries/use-insurance-payer";
import {
  FeeScheduleCodeTypeEnum,
  FeeScheduleFeeScheduleTypeEnum,
  GetMedicalCodesTypeEnum,
} from "../../../../services";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  transformMultiSelectBackEndVal,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { NO_NUMBER_ARROWS_MUI } from "../../../../utils/global-styles";
import AsyncSelectScroll from "../../../AsyncSelectScroll";
import CustomDatePicker from "../../../CustomDatePicker";
import CustomSelect from "../../../CustomSelect";
import CustomSelectScroll from "../../../CustomSelectScroll";
import ErrorHelper from "../../../ErrorWrapper";
import Flex from "../../../Flex";
import SideSheetDrawer from "../../../SidesheetDrawer";
import NumberInput from "../../../NumberInput";
import { getStoredRealm } from "@/utils/auth-storage";

interface UpsertFeeScheduleModalProps {
  disclosureHost: DisclosureHost;
  curFormState?: string;
}

type SelectVal = { value: string; label: string };

type FeeSchedule = {
  feeScheduleType?: FeeScheduleFeeScheduleTypeEnum;
  modifiers?: SelectVal[];
  amount?: string;
  allowedAmount?: string;
  // Named to match the FeeSchedule API field; the payload/response carry
  // `codeType`, not `type`.
  codeType?: FeeScheduleCodeTypeEnum;
  // Payer-Specific fee schedules allow a single Provider and Payer.
  providers?: SelectVal;
  payers?: SelectVal;
  medicalCodes?: SelectVal;
  medicalCodeDescription?: string;
  fromDate?: Dayjs | null;
  endDate?: Dayjs | null;
  active?: boolean;
};

const STATUS_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const initialValues: FeeSchedule = {
  feeScheduleType: "PAYER_SPECIFIC",
  modifiers: [],
  amount: "",
  allowedAmount: "",
  codeType: FeeScheduleCodeTypeEnum.Cpt,
  providers: undefined,
  payers: undefined,
  medicalCodes: undefined,
  fromDate: null,
  endDate: null,
  active: true,
};

const UpsertFeeScheduleModal = ({
  disclosureHost,
  curFormState,
}: UpsertFeeScheduleModalProps) => {
  const { mutateAsync: createFeeSchedule } = useCreateFeeSchedule();
  const { mutateAsync: updateFeeSchedule } = useUpdateFeeSchedule();
  const [modifierSearchKey, setModifierSearchKey] = useState<string>("");
  const [modifierPagination, setModifierPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [payerSearchKey, setPayerSearchKey] = useDebounce<string>("");
  const [searchProvider, setSearchProvider] = useDebounce<string>("");
  const [procedureCodeSearch, setProcedureCodeSearch] = useDebounce<string>("");
  const [codePagination, setCodePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const creteFeeScheduleHandler = (v: FeeSchedule) => {
    const isPayerSpecific =
      v?.feeScheduleType === FeeScheduleFeeScheduleTypeEnum.PayerSpecific;

    const medicalCodes = v?.medicalCodes
      ? { [v.medicalCodes.value]: v.medicalCodes.label }
      : undefined;

    const modifier =
      v?.modifiers && v.modifiers.length > 0
        ? transformMultiSelectBackEndVal(v.modifiers.filter(Boolean) as any)
        : undefined;

    const providers = v?.providers
      ? transformMultiSelectBackEndVal([v.providers])
      : undefined;
    const payers =
      isPayerSpecific && v?.payers
        ? transformMultiSelectBackEndVal([v.payers])
        : undefined;

    const payload = {
      // include uuid on edit so the backend updates the existing record
      ...(disclosureHost?.data?.uuid ? { uuid: disclosureHost.data.uuid } : {}),
      feeScheduleType: v.feeScheduleType,
      codeType: v.codeType,
      medicalCodes,
      medicalCodeDescription: v.medicalCodeDescription,
      modifier,
      providers,
      payers,
      amount: v.amount,
      allowedAmount: isPayerSpecific ? v.allowedAmount : undefined,
      fromDate: v?.fromDate,
      endDate: v?.endDate,
      active: v.active,
    };

    const transFormApiPayload = {
      feeSchedule: payload,
      xTENANTID: getXtenantId(),
    };

    showToastAndReset(
      disclosureHost?.data
        ? updateFeeSchedule(transFormApiPayload as any)
        : createFeeSchedule(transFormApiPayload as any)
    );
  };

  const { data: modifierData, isFetching: isFetchingModifiers } =
    useGetModifiers({
      searchString: modifierSearchKey,
      page: modifierPagination.page,
      size: modifierPagination.pageSize,
      xTENANTID: getXtenantId(),
    });

  function closeAndReset() {
    disclosureHost.close();
    formik.resetForm();
  }

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["fee-schedule-list"] });
        setTimeout(closeAndReset, 1500);
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const editData = disclosureHost?.data;
  const formik = useFormik<FeeSchedule>({
    initialValues: editData
      ? {
          ...initialValues,
          ...editData,
          feeScheduleType: editData?.feeScheduleType ?? undefined,
          // Take the saved code type as-is. Falling through to the "CPT"
          // default of `initialValues` made every HCPCS record read as CPT.
          codeType: editData?.codeType ?? undefined,
          modifiers: editData?.modifier
            ? (transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                editData.modifier
              ) as SelectVal[])
            : [],
          payers: editData?.payers
            ? (
                transformMultiSelectFrontEndVal(
                  DEFAULT_VALUE_LABEL_LIST,
                  editData.payers
                ) as SelectVal[]
              )?.[0]
            : undefined,
          providers: editData?.providers
            ? (
                transformMultiSelectFrontEndVal(
                  DEFAULT_VALUE_LABEL_LIST,
                  editData.providers
                ) as SelectVal[]
              )?.[0]
            : undefined,
          fromDate: editData?.fromDate ? dayjs(editData.fromDate) : null,
          endDate: editData?.endDate ? dayjs(editData.endDate) : null,
          active: editData?.active ?? true,
        }
      : initialValues,
    validationSchema: Yup.object().shape({
      feeScheduleType: Yup.string()
        .required("Please select a fee schedule type")
        .label("Fee Schedule Type"),
      medicalCodes: Yup.object().nullable().required().label("Procedure Code"),
      providers: Yup.object().nullable().required("Provider is required"),
      amount: Yup.mixed()
        .nullable()
        .test("amount-required", "Charge is required", (val) => {
          if (val === undefined || val === null || val === "") return false;
          return !isNaN(Number(val));
        })
        .test("amount-positive", "Charge must be greater than 0", (val) => {
          if (val === undefined || val === null || val === "") return true;
          return Number(val) > 0;
        }),
      payers: Yup.object()
        .nullable()
        .test("payers-required", "Payer is required", function (val) {
          if (
            this.parent.feeScheduleType !==
            FeeScheduleFeeScheduleTypeEnum.PayerSpecific
          )
            return true;
          return !!val;
        }),
      allowedAmount: Yup.mixed()
        .nullable()
        .test("allowed-required", "Allowed Amount is required", function (val) {
          if (
            this.parent.feeScheduleType !==
            FeeScheduleFeeScheduleTypeEnum.PayerSpecific
          )
            return true;
          if (val === undefined || val === null || val === "") return false;
          return !isNaN(Number(val));
        })
        .test(
          "allowed-non-negative",
          "Allowed Amount cannot be less than 0.",
          function (val) {
            if (
              this.parent.feeScheduleType !==
              FeeScheduleFeeScheduleTypeEnum.PayerSpecific
            )
              return true;
            if (val === undefined || val === null || val === "") return true;
            return Number(val) >= 0;
          }
        )
        .test(
          "allowed-not-greater-than-billed",
          "Allowed Amount cannot be greater than the Billed Charge.",
          function (val) {
            if (
              this.parent.feeScheduleType !==
              FeeScheduleFeeScheduleTypeEnum.PayerSpecific
            )
              return true;
            if (val === undefined || val === null || val === "") return true;
            const billed = this.parent.amount;
            // Skip until Billed Charge is a valid number; its own rule handles that.
            if (billed === undefined || billed === null || billed === "")
              return true;
            if (isNaN(Number(billed)) || isNaN(Number(val))) return true;
            return Number(val) <= Number(billed);
          }
        ),
      fromDate: Yup.mixed()
        .nullable()
        .test("from-required", "From Date is required", (val) => !!val),
      endDate: Yup.mixed()
        .nullable()
        .test("end-required", "End Date is required", (val) => !!val)
        .test(
          "end-after-from",
          "End Date must be greater than or equal to From Date",
          function (val) {
            const { fromDate } = this.parent;
            if (!val || !fromDate) return true;
            return (
              dayjs(val as any).isSame(dayjs(fromDate)) ||
              dayjs(val as any).isAfter(dayjs(fromDate))
            );
          }
        )
        .test(
          "end-not-expired",
          "End Date cannot be in the past; the fee schedule would already be expired",
          (val) => {
            // Only enforce while adding a new fee schedule. Existing records are
            // left editable so an already-expired schedule can still be updated.
            if (editData) return true;
            if (!val) return true;
            return (
              dayjs(val as any).isSame(dayjs(), "day") ||
              dayjs(val as any).isAfter(dayjs(), "day")
            );
          }
        ),
    }),
    onSubmit: creteFeeScheduleHandler,
    enableReinitialize: true,
  });

  const feeScheduleType = formik.values.feeScheduleType;
  const isPayerSpecific =
    feeScheduleType === FeeScheduleFeeScheduleTypeEnum.PayerSpecific;
  const isProviderSpecific =
    feeScheduleType === FeeScheduleFeeScheduleTypeEnum.ProviderSpecific;
  const showFields = isPayerSpecific || isProviderSpecific;

  const [modalProviderPagination, setModalProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [modalPayerPagination, setModalPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: providersList, isFetching: modalProviderFetching } =
    useProvidersList({
      status: true,
      searchKey: searchProvider,
      pagination: modalProviderPagination,
      xTENANTID: getStoredRealm() ?? "",
    });

  const { data: payersList, isFetching: modalPayerFetching } =
    useInsurancePayerList({
      searchKey: payerSearchKey,
      xTENANTID: getXtenantId(),
      pagination: modalPayerPagination,
    });

  const {
    data: codes,
    isLoading: isCodesLoading,
    isFetching: isCodesFetching,
  } = useGetCataLogCode({
    pagination: codePagination,
    searchKey: procedureCodeSearch,
    type: (formik.values?.codeType as GetMedicalCodesTypeEnum) || "CPT",
    active: true,
    archive: false,
  });

  const disabled = curFormState === "view";

  // Procedure Code and Provider identify an existing fee schedule record, so
  // they are locked once it is created and can only be set while adding. Code
  // Type is locked with them because switching it clears the Procedure Code.
  const isKeyFieldLocked = disabled || !!editData;

  // The provider/payer lists are paginated (and re-fetched on search), so the
  // value saved on a record is often absent from the fetched page. The select
  // resolves its display label by looking the value up in `options`, so keep the
  // already-selected option in the list or the field renders empty on edit.
  const withSelectedOption = (options: SelectVal[], selected?: SelectVal) =>
    selected?.value && !options.some((o) => o.value === selected.value)
      ? [selected, ...options]
      : options;

  const selectedProvider = formik.values.providers;
  const selectedPayer = formik.values.payers;

  const providerOptions = useMemo(
    () =>
      withSelectedOption(
        providersList?.data?.content?.map((it) => ({
          value: it?.uuid ?? "",
          label: `${it?.firstName ?? ""} ${it?.lastName ?? ""}`.trim(),
        })) ?? [],
        selectedProvider
      ),
    [providersList?.data?.content, selectedProvider]
  );

  const payerOptions = useMemo(
    () =>
      withSelectedOption(
        payersList?.data?.content?.map((it) => ({
          value: it?.payerId ?? "",
          label: it?.payerName ?? "",
        })) ?? [],
        selectedPayer
      ),
    [payersList?.data?.content, selectedPayer]
  );

  const setFeeScheduleType = (type: FeeScheduleFeeScheduleTypeEnum) => {
    formik.setFieldValue("feeScheduleType", type);
  };

  const amountLabel = isProviderSpecific ? "Charge Amount" : "Billed Charge";

  // Block keys that would produce a negative / non-decimal value so amount
  // fields can never hold a negative number at the input level.
  const blockNegativeKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
  };

  // Currency symbol shown while typing, so the amount inputs match how the
  // saved values are rendered in the fee schedule grid.
  const dollarAdornment = {
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  };

  return (
    <SideSheetDrawer
      onClose={closeAndReset}
      open={disclosureHost.opened}
      title={
        curFormState === "view"
          ? "View Fee Schedule"
          : disclosureHost?.data
            ? "Edit Fee Schedule"
            : "Add Fee Schedule"
      }
      w="1143px"
      footerActions={
        curFormState !== "view" ? (
          <Button onClick={formik.submitForm} variant="contained">
            Save
          </Button>
        ) : undefined
      }
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ErrorHelper
              error={
                formik.touched.feeScheduleType
                  ? (formik.errors.feeScheduleType as string)
                  : undefined
              }
            >
              <Flex gap={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={disabled}
                      checked={isPayerSpecific}
                      onChange={() =>
                        setFeeScheduleType(
                          FeeScheduleFeeScheduleTypeEnum.PayerSpecific
                        )
                      }
                    />
                  }
                  label="Payer Specific"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={disabled}
                      checked={isProviderSpecific}
                      onChange={() =>
                        setFeeScheduleType(
                          FeeScheduleFeeScheduleTypeEnum.ProviderSpecific
                        )
                      }
                    />
                  }
                  label="Provider Specific"
                />
              </Flex>
            </ErrorHelper>
          </Grid>

          {showFields && (
            <>
              {isPayerSpecific && (
                <Grid item xs={4}>
                  <CustomSelectScroll
                    name="payers"
                    label="Payer"
                    required
                    readOnly={disabled}
                    placeholder="Select Payer"
                    options={payerOptions}
                    value={formik.values.payers?.value ?? undefined}
                    onChange={(_, opt) => {
                      formik.setFieldValue("payers", opt ?? undefined);
                    }}
                    onSearch={(_, val) => {
                      setPayerSearchKey(val);
                      setModalPayerPagination((p) => ({ ...p, page: 0 }));
                    }}
                    enableFilterOption
                    handleBlur={formik.handleBlur}
                    error={
                      formik.touched.payers
                        ? (formik.errors.payers as string)
                        : undefined
                    }
                    loading={modalPayerFetching}
                    pagination={modalPayerPagination}
                    setPagination={setModalPayerPagination}
                    totalElements={payersList?.data?.page?.totalElements}
                  />
                </Grid>
              )}

              <Grid item xs={4}>
                <CustomSelect
                  readOnly={isKeyFieldLocked}
                  label="Code Type"
                  options={[
                    { value: "CPT", label: "CPT" },
                    { value: "HCPCS", label: "HCPCS" },
                  ]}
                  placeholder="Select Code Type"
                  value={formik.values?.codeType}
                  onChange={(value) => {
                    formik.setFieldValue("codeType", value);
                    formik.setFieldValue("medicalCodes", undefined);
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <AsyncSelectScroll
                  disabled={isKeyFieldLocked}
                  required
                  loading={isCodesLoading || isCodesFetching}
                  label="Procedure Code"
                  options={codes?.data?.content?.map((it) => ({
                    value: it?.uuid,
                    label: `${it?.code} - ${it?.description}`,
                  }))}
                  handleBlur={formik.handleBlur}
                  placeholder="Select Procedure Code"
                  onSearch={(v) => {
                    setProcedureCodeSearch(v);
                    setCodePagination((p) => ({ ...p, page: 0 }));
                  }}
                  value={formik.values?.medicalCodes}
                  onChange={(value) => {
                    formik.setFieldValue("medicalCodes", value);
                    formik.setFieldValue(
                      "medicalCodeDescription",
                      value?.label?.split("-")?.[1]
                    );
                  }}
                  error={
                    formik.touched.medicalCodes && formik.errors.medicalCodes
                  }
                  pagination={codePagination}
                  setPagination={setCodePagination}
                  totalElements={codes?.data?.page?.totalElements}
                />
              </Grid>

              <Grid item xs={4}>
                <CustomSelectScroll
                  name="providers"
                  label="Provider"
                  required
                  readOnly={isKeyFieldLocked}
                  placeholder="Select Provider"
                  options={providerOptions}
                  value={formik.values.providers?.value ?? undefined}
                  onChange={(_, opt) => {
                    formik.setFieldValue("providers", opt ?? undefined);
                  }}
                  onSearch={(_, val) => {
                    setSearchProvider(val);
                    setModalProviderPagination((p) => ({ ...p, page: 0 }));
                  }}
                  handleBlur={formik.handleBlur}
                  error={
                    formik.touched.providers
                      ? (formik.errors.providers as string)
                      : undefined
                  }
                  loading={modalProviderFetching}
                  pagination={modalProviderPagination}
                  setPagination={setModalProviderPagination}
                  totalElements={providersList?.data?.page?.totalElements}
                />
              </Grid>

              <Grid item xs={4} alignContent="flex-start">
                <NumberInput
                  required
                  label={amountLabel}
                  textFieldProps={{
                    disabled,
                    // type: "number",
                    name: "amount",
                    value: formik.values?.amount,
                    onChange: formik.handleChange,
                    placeholder: `Enter ${amountLabel}`,
                    onBlur: formik.handleBlur,
                    onKeyDown: blockNegativeKeys,
                    inputProps: { min: 0 },
                    InputProps: dollarAdornment,
                    sx: NO_NUMBER_ARROWS_MUI,
                  }}
                  error={formik.touched.amount && formik.errors.amount}
                />
              </Grid>

              {isPayerSpecific && (
                <Grid item xs={4} alignContent="flex-start">
                  <NumberInput
                    required
                    label="Allowed Amount"
                    textFieldProps={{
                      disabled,
                      // type: "number",
                      name: "allowedAmount",
                      value: formik.values?.allowedAmount,
                      onChange: formik.handleChange,
                      placeholder: "Enter Allowed Amount",
                      onBlur: formik.handleBlur,
                      onKeyDown: blockNegativeKeys,
                      inputProps: { min: 0 },
                      InputProps: dollarAdornment,
                      sx: NO_NUMBER_ARROWS_MUI,
                    }}
                    error={
                      formik.touched.allowedAmount &&
                      formik.errors.allowedAmount
                    }
                  />
                </Grid>
              )}

              <Grid item xs={4}>
                <CustomDatePicker
                  label="From Date"
                  required
                  disabled={disabled}
                  value={formik.values.fromDate}
                  onChange={(val) => formik.setFieldValue("fromDate", val)}
                  handleBlur={formik.handleBlur}
                  name="fromDate"
                  error={
                    formik.touched.fromDate
                      ? (formik.errors.fromDate as string)
                      : undefined
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <CustomDatePicker
                  label="End Date"
                  required
                  disabled={disabled}
                  value={formik.values.endDate}
                  onChange={(val) => formik.setFieldValue("endDate", val)}
                  handleBlur={formik.handleBlur}
                  name="endDate"
                  minDate={
                    formik.values.fromDate
                      ? dayjs(formik.values.fromDate)
                      : undefined
                  }
                  error={
                    formik.touched.endDate
                      ? (formik.errors.endDate as string)
                      : undefined
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <CustomSelect
                  readOnly={disabled}
                  label="Status"
                  required
                  options={STATUS_OPTIONS}
                  placeholder="Select Status"
                  value={formik.values.active ? "true" : "false"}
                  onChange={(value) =>
                    formik.setFieldValue("active", value === "true")
                  }
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <ErrorHelper label="Modifier">
              <Flex gap={0.4}>
                {Array.from({ length: 4 }).map((_, id) => (
                  <Box key={id} width={"25%"}>
                    <AsyncSelectScroll
                      forcePopupIcon={false}
                      loading={isFetchingModifiers}
                      pagination={modifierPagination}
                      setPagination={setModifierPagination}
                      totalElements={modifierData?.data?.page?.totalElements}
                      onSearch={(val) => {
                        setModifierSearchKey(val);
                        setModifierPagination((p) => ({ ...p, page: 0 }));
                      }}
                      options={modifierData?.data?.content?.map((it) => ({
                        value: it.uuid,
                        label: `${it.code}-${it.description ?? ""}`,
                      }))}
                      value={formik.values?.modifiers?.[id] as any}
                      onChange={(value) => {
                        // Prevent the same modifier being picked in more than one
                        // of the 4 slots for a single CPT. O(n) membership check.
                        const selectedId = value?.value;
                        if (
                          selectedId &&
                          formik.values?.modifiers?.some(
                            (m, i) => i !== id && m?.value === selectedId
                          )
                        ) {
                          showSnackbar({
                            message: "This modifier is already selected",
                            type: "error",
                          });
                          return;
                        }
                        formik.setFieldValue(`modifiers.[${id}]` as any, value);
                      }}
                    />
                  </Box>
                ))}
              </Flex>
            </ErrorHelper>
          </Grid>
        </Grid>
      </Box>
    </SideSheetDrawer>
  );
};

export default UpsertFeeScheduleModal;
